import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Eye, EyeOff, Loader2, ArrowLeft, Mail, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";
import { LanguageSelector } from "@/components/LanguageSelector";

const API_URL = "https://api.geocheckapp.com";

export const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, isRTL } = useLanguage();
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotEmailSent, setForgotEmailSent] = useState(false);
  
  // Check for employer invite code in URL
  const employerInviteCode = searchParams.get('employer_invite');
  const [invitedCompanyName, setInvitedCompanyName] = useState(null);
  
  // Look up company name if employer invite code is present
  useEffect(() => {
    if (employerInviteCode) {
      setIsLogin(false); // Switch to register mode for invites
      fetch(`${API_URL}/api/company/by-invite/${employerInviteCode}`)
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data) {
            setInvitedCompanyName(data.name);
          }
        })
        .catch(() => {});
    }
  }, [employerInviteCode]);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    confirmPassword: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email })
      });

      if (res.ok) {
        setForgotEmailSent(true);
        toast.success(t("resetEmailSent") || "Password reset email sent!");
      } else {
        const data = await res.json();
        toast.error(data.detail || t("resetEmailFailed") || "Failed to send reset email");
      }
    } catch (error) {
      toast.error(t("networkError") || "Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!isLogin) {
        // Registration validation
        if (formData.password !== formData.confirmPassword) {
          toast.error(t("passwordsDoNotMatch") || "Passwords do not match");
          setLoading(false);
          return;
        }
        if (formData.password.length < 6) {
          toast.error(t("passwordTooShort") || "Password must be at least 6 characters");
          setLoading(false);
          return;
        }
        if (!formData.name.trim()) {
          toast.error(t("nameRequired") || "Name is required");
          setLoading(false);
          return;
        }
      }

      const endpoint = isLogin ? "/api/auth/login" : "/api/auth/register";
      const body = isLogin 
        ? { email: formData.email, password: formData.password }
        : { email: formData.email, password: formData.password, name: formData.name };

      const res = await fetch(`${API_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body)
      });

      const data = await res.json();

      if (!res.ok) {
        toast.error(data.detail || (isLogin ? t("loginFailed") : t("registrationFailed")) || "Authentication failed");
        setLoading(false);
        return;
      }

      toast.success(isLogin ? (t("loginSuccess") || "Welcome back!") : (t("accountCreated") || "Account created!"));

      // Redirect based on role (admin goes to their role dashboard, with admin access in settings)
      if (data.role === "employer") {
        navigate("/employer");
      } else if (data.role === "employee") {
        navigate("/employee");
      } else if (data.is_admin && !data.role) {
        // Pure admin (no company role) goes to admin dashboard
        navigate("/admin");
      } else {
        // Pass employer invite code to setup page if present
        navigate("/setup", { 
          state: { 
            user: data,
            employerInviteCode: employerInviteCode,
            invitedCompanyName: invitedCompanyName
          } 
        });
      }
    } catch (error) {
      toast.error(t("networkError") || "Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-background to-accent/20 flex items-center justify-center p-4 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      
      {/* Employer Invite Banner */}
      {employerInviteCode && invitedCompanyName && !isForgotPassword && (
        <div className="fixed top-0 left-0 right-0 bg-blue-600 text-white py-3 px-4 text-center z-50">
          <div className="flex items-center justify-center gap-2">
            <Building2 className="w-5 h-5" />
            <span className="font-medium">
              {t("employerInviteBanner") || "You've been invited to join"} <strong>{invitedCompanyName}</strong> {t("asEmployer") || "as an employer"}
            </span>
          </div>
        </div>
      )}
      
      {isForgotPassword ? (
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
                <Mail className="w-8 h-8 text-primary-foreground" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">{t("forgotPassword") || "Forgot Password"}</CardTitle>
            <CardDescription>
              {forgotEmailSent
                ? (t("checkYourEmail") || "Check your email for the reset link")
                : (t("forgotPasswordDesc") || "Enter your email and we'll send you a reset link")
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {forgotEmailSent ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                  <Mail className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-muted-foreground">
                  {t("resetEmailSentDesc") || "We've sent a password reset link to your email address. Please check your inbox."}
                </p>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setIsForgotPassword(false);
                    setForgotEmailSent(false);
                  }}
                  className="w-full"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t("backToLogin") || "Back to Login"}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">{t("email") || "Email"}</Label>
                  <Input
                    id="forgot-email"
                    name="email"
                    type="email"
                    placeholder={t("enterEmail") || "Enter your email"}
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading}
                  data-testid="send-reset-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("sending") || "Sending..."}</>
                  ) : (
                    t("sendResetLink") || "Send Reset Link"
                  )}
                </Button>
                
                <Button 
                  type="button"
                  variant="ghost" 
                  onClick={() => setIsForgotPassword(false)}
                  className="w-full"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t("backToLogin") || "Back to Login"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      ) : (
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
              <MapPin className="w-8 h-8 text-primary-foreground" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold">{t("appName") || "GeoCheck"}</CardTitle>
          <CardDescription>
            {isLogin 
              ? (t("loginDescription") || "Sign in to your account")
              : (t("registerDescription") || "Create a new account")
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="name">{t("fullName") || "Full Name"}</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder={t("enterName") || "Enter your name"}
                  value={formData.name}
                  onChange={handleChange}
                  required={!isLogin}
                  data-testid="auth-name-input"
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email">{t("email") || "Email"}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder={t("enterEmail") || "Enter your email"}
                value={formData.email}
                onChange={handleChange}
                required
                data-testid="auth-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">{t("password") || "Password"}</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder={t("enterPassword") || "Enter password"}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="auth-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {isLogin && (
                <button
                  type="button"
                  onClick={() => {
                    setIsForgotPassword(true);
                    setForgotEmailSent(false);
                  }}
                  className="text-sm text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  {t("forgotPassword") || "Forgot password?"}
                </button>
              )}
            </div>
            
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t("confirmPassword") || "Confirm Password"}</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder={t("confirmPasswordPlaceholder") || "Confirm your password"}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required={!isLogin}
                  data-testid="auth-confirm-password-input"
                />
              </div>
            )}
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("pleaseWait") || "Please wait..."}</>
              ) : (
                isLogin ? (t("signIn") || "Sign In") : (t("createAccount") || "Create Account")
              )}
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              {isLogin 
                ? (t("noAccount") || "Don't have an account?")
                : (t("haveAccount") || "Already have an account?")
              }
              {" "}
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setFormData({ email: "", password: "", name: "", confirmPassword: "" });
                }}
                className="text-primary hover:underline font-medium"
                data-testid="auth-toggle-btn"
              >
                {isLogin ? (t("signUp") || "Sign up") : (t("signIn") || "Sign in")}
              </button>
            </p>
          </div>
          
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              ← {t("backToHome") || "Back to home"}
            </button>
          </div>
        </CardContent>
      </Card>
      )}
    </div>
  );
};

export default AuthPage;
